// src/CollectionModal.js
// Unified "Add to Collection" modal — one entry point for all card-copy editing.
// See docs/UNIFIED_MODAL_SPEC.md for the full spec and known tech debt.

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { parallelKey } from './parallelData';
import { getParallelsForSet } from './sets/parallelsConfig';
import { imagePath, imageUrl, handlePhotoRemove, updateMarketplaceEligibility, CONDITION_SLOTS, CONDITION_CATEGORIES, usePhotoSource, usePhotoDrop, canDropPhotos, swallowDrops, removeExtraPhoto, decodeImage, isLandscapeCard } from './CardImage';
import { cropToCard } from './cardCrop';
import VerificationGateModal from './components/VerificationGateModal';
import { fetchNote, saveNote, NOTE_MAX } from './lib/collectionNotes';
import { trackCardMarked } from './activityTracker';

// Phase 2E.1 § 5.6 Decision 6: hard verification gate at this listing price.
const VERIFICATION_GATE_THRESHOLD = 500;

// ---- Color tokens (match brand guide) ----
const C = {
  gold: 'var(--gold)',
  cyan: 'var(--cyan)',
  green: 'var(--green)',
  purple: 'var(--purple)',
  orange: 'var(--sketch-orange)',
  red: 'var(--red-2)',
  muted: 'var(--muted-2)',
  // Private-notes accent. Deliberately the quietest colour on the panel: every
  // other section accent belongs to something public or transactional, and
  // purple already means FOR SALE in the status picker.
  steel: 'var(--muted)',
  cardBg: 'var(--panel)',
  uiBg: 'var(--bg)',
  border: 'var(--border)',
  text: 'var(--text)',
  textDim: 'var(--muted-3)',
};

// ---- Shared style fragments ----
const fontDisplay = "'Orbitron', sans-serif";
const fontBody = "'Rajdhani', sans-serif";
const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
const fontMono = "'Share Tech Mono', monospace";

const GRADING_COMPANIES = ['PSA', 'BGS', 'CGC', 'TAG', 'SGC', 'Other'];

// =====================================================================
// Image resize helper (keeps uploads small — matches existing CardImage convention)
// =====================================================================
// autoPortrait: rotate landscape photos 90° counterclockwise so cards always
// land portrait. Front/back only — condition close-ups (e.g. Border Top) are
// legitimately landscape and must not be rotated.
// Decodes via createImageBitmap (large-image safe — see CardImage.decodeImage)
// so full-res Android camera JPEGs resize reliably, and falls back to the
// original file on any decode/encode failure rather than rejecting (which on
// Android surfaced as a failed save).
async function resizeImage(file, maxDim = 1600, opts = {}) {
  return (await resizePhoto(file, maxDim, opts)).blob;
}

// autoCrop: crop front/back photos to the card (see cardCrop.js). Resolves
// { blob, cropped } so the window can show "auto-cropped · undo". The crop is
// deterministic, so the preview and the save-time re-encode match.
async function resizePhoto(file, maxDim = 1600, { autoPortrait = false, autoCrop = false } = {}) {
  try {
    const decoded = await decodeImage(file);
    const { src, cropped } = autoCrop ? cropToCard(decoded) : { src: decoded, cropped: false };
    if (cropped && typeof decoded.close === 'function') decoded.close();
    const rotate = autoPortrait && src.width > src.height;
    let width = rotate ? src.height : src.width;
    let height = rotate ? src.width : src.height;
    if (width > maxDim || height > maxDim) {
      const ratio = Math.min(maxDim / width, maxDim / height);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (rotate) {
      ctx.translate(0, height);
      ctx.rotate(-Math.PI / 2);
      ctx.drawImage(src, 0, 0, height, width);
    } else {
      ctx.drawImage(src, 0, 0, width, height);
    }
    if (typeof src.close === 'function') src.close(); // release ImageBitmap memory
    return await new Promise((resolve) => {
      canvas.toBlob((blob) => resolve({ blob: blob || file, cropped: !!blob && cropped }), 'image/jpeg', 0.88);
    });
  } catch (_e) {
    return { blob: file, cropped: false };
  }
}

// =====================================================================
// MAIN COMPONENT
// =====================================================================
export default function CollectionModal({
  isOpen,
  onClose,
  user,
  setMeta,           // { id: '2024-topps-chrome' } — parent set container
  set,               // { id: 'chrome-galaxy', cards: [...] } — the actual set
  card,              // { n: '7', name: 'Anakin...', artist: '...' }
  mode = 'new',      // 'new' | 'edit'
  initialParallelId, // for new: which parallel to default to. for edit: the locked parallel
  existingEntry,     // for edit: { cardKey, state, gradings, serial_num, sale_price, trade_value, ... }
  onSaved,           // callback after successful save
  initialSerial,     // for new: pre-fill the serial number (e.g. from the AI scanner)
  initialPhotoFront, // for new: a File to pre-load as the front photo (AI scanner)
  initialPhotoBack,  // for new: a File to pre-load as the back photo (AI scanner)
}) {
  // --- core form state ---
  const [status, setStatus] = useState('collected');
  const [photoChooser, setPhotoChooser] = useState(null); // 'front' | 'back' | null - photo source chooser
  const [parallelId, setParallelId] = useState(initialParallelId || 'base');
  const [parallelDropdownOpen, setParallelDropdownOpen] = useState(false);
  const [serial, setSerial] = useState('');

  // photos: each is either null, a File object (pending upload), or 'existing' (already saved)
  const [photoFront, setPhotoFront] = useState(null);
  const [photoBack, setPhotoBack] = useState(null);
  const [photoFrontUrl, setPhotoFrontUrl] = useState(null); // preview URL
  const [photoBackUrl, setPhotoBackUrl] = useState(null);
  // Auto-crop per side for a pending File: null = nothing was cropped,
  // 'auto' = shown cropped (offer undo), 'off' = collector undid it.
  const [photoCrop, setPhotoCrop] = useState({ front: null, back: null });

  // Optional condition close-ups, keyed by slot:
  //   condPhotos[slot]    = File (pending) | 'existing' | undefined
  //   condPhotoUrls[slot] = preview/remote URL | undefined
  const [condPhotos, setCondPhotos] = useState({});
  const [condPhotoUrls, setCondPhotoUrls] = useState({});

  const [salePrice, setSalePrice] = useState('');
  const [tradeValue, setTradeValue] = useState('');
  const [tradeNotes, setTradeNotes] = useState('');
  const [willingToPay, setWillingToPay] = useState('');

  // Private note — lives in collection_notes, not on the collections row.
  const [privateNote, setPrivateNote] = useState('');

  // grade fields
  const [gradeCompany, setGradeCompany] = useState('');
  const [gradeValue, setGradeValue] = useState('');
  const [gradeCertNum, setGradeCertNum] = useState('');
  const [gradeTagScore, setGradeTagScore] = useState('');
  const [gradePrintRun, setGradePrintRun] = useState('');
  const [gradePopThis, setGradePopThis] = useState('');
  const [gradePopHigher, setGradePopHigher] = useState('');
  const [gradeReportUrl, setGradeReportUrl] = useState('');
  const [advancedGradingOpen, setAdvancedGradingOpen] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Phase 2E.1: identity_verified gates the $500+ publish flow.
  // null = unknown (still fetching). Booleans drive UX after fetch resolves.
  const [identityVerified, setIdentityVerified] = useState(null);
  const [gateModalProps, setGateModalProps] = useState(null); // { cardKey, priceUsd } when open
  // Phase 2E.2 § 5.2 Addendum: For Sale listings require ≥1 payment method.
  // null = unknown (still fetching); { paypal: bool, venmo: bool } once loaded.
  const [paymentMethods, setPaymentMethods] = useState(null);

  // Cancels an in-flight private-note fetch when the modal closes or switches card.
  const noteFetchCancel = useRef(null);
  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);
  // Separate inputs for the Android chooser: camera-only and library-only.
  // We keep the originals above for iPhone (whose native iOS sheet handles all sources cleanly)
  // and avoid mutating the `capture` attribute at click time, which races on Android Chrome.
  const frontCameraInputRef = useRef(null);
  const frontLibraryInputRef = useRef(null);
  const backCameraInputRef = useRef(null);
  const backLibraryInputRef = useRef(null);

  // Optional condition close-ups reuse the shared picker (handles the iOS/Android
  // camera-vs-library quirks) for the corners/edges/surface slots.
  const handleCondFile = async (slot, file) => {
    if (!file) return;
    setCondPhotos((p) => ({ ...p, [slot]: file }));
    // Downscaled preview (see handlePhotoSelect) so large camera shots don't
    // render blank; fall back to the raw file.
    let url;
    try {
      url = URL.createObjectURL(await resizeImage(file));
    } catch (_e) {
      url = URL.createObjectURL(file);
    }
    setCondPhotoUrls((p) => ({ ...p, [slot]: url }));
  };
  const {
    openPicker: openCondPicker,
    inputs: condInputs,
    chooser: condChooser,
  } = usePhotoSource(handleCondFile, CONDITION_SLOTS.map((s) => s.slot));

  // Booklet cards (see isLandscapeCard in CardImage.js): photos stay landscape
  // — no autoPortrait rotation — and the front/back frames render wide.
  const isLandscape = !!(setMeta && set) && isLandscapeCard(`${setMeta.id}__${set.id}__`);

  // --- load parallel metadata for this set ---
  const parallels = useMemo(() => {
    if (!setMeta || !set) return [];
    try {
      return getParallelsForSet(setMeta.id, set.id, set) || [];
    } catch (e) {
      console.error('getParallelsForSet failed:', e);
      return [];
    }
  }, [setMeta, set]);

  const currentParallel = useMemo(
    () => parallels.find(p => p.id === parallelId) || { id: 'base', name: 'Base', print_run: null },
    [parallels, parallelId]
  );

  const isSerializedParallel = !!(currentParallel.print_run && currentParallel.print_run > 1);

  // --- hydrate state when editing ---
  useEffect(() => {
    if (!isOpen) return;
    setPhotoCrop({ front: null, back: null });
    if (mode === 'edit' && existingEntry) {
      setStatus(existingEntry.status || 'collected');
      setSerial(existingEntry.grading_serial_num ? String(existingEntry.grading_serial_num) : '');
      setSalePrice(existingEntry.sale_price != null ? String(existingEntry.sale_price) : '');
      setTradeValue(existingEntry.trade_value != null ? String(existingEntry.trade_value) : '');
      setTradeNotes(existingEntry.trade_notes || '');
      setWillingToPay(existingEntry.willing_to_pay != null ? String(existingEntry.willing_to_pay) : '');
      setGradeCompany(existingEntry.grading_company || '');
      setGradeValue(existingEntry.grading_grade || '');
      setGradeCertNum(existingEntry.grading_cert_num || '');
      setGradeTagScore(existingEntry.grading_tag_score || '');
      setGradePrintRun(existingEntry.grading_print_run || '');
      setGradePopThis(existingEntry.grading_pop_this || '');
      setGradePopHigher(existingEntry.grading_pop_higher || '');
      setGradeReportUrl(existingEntry.grading_report_url || '');

      // Private note lives in its own table, so it is fetched rather than read
      // off existingEntry. Guarded against a stale resolve if the modal is
      // closed or switched to another card mid-flight.
      if (existingEntry.cardKey && user?.id) {
        let cancelled = false;
        fetchNote(user.id, existingEntry.cardKey)
          .then((n) => { if (!cancelled) setPrivateNote(n); });
        noteFetchCancel.current = () => { cancelled = true; };
      }

      // Load existing photos for preview.
      // We need to actually check if the photo file exists in storage (not just
      // assume from existingEntry.has_photos which can be stale). We also use
      // a cache-buster from photos_updated_at so Safari doesn't serve stale bytes.
      const cardKey = existingEntry.cardKey;
      if (cardKey && user?.id) {
        const version = existingEntry.photos_updated_at || existingEntry.has_photos;
        const fUrl = imageUrl(user.id, cardKey, 'front', version);
        const bUrl = imageUrl(user.id, cardKey, 'back', version);
        // Probe each URL with a HEAD-equivalent fetch — only mark 'existing' if
        // the file actually responds successfully.
        const probe = async (url, side) => {
          try {
            const resp = await fetch(url, { method: 'GET', cache: 'no-store' });
            if (resp.ok) {
              if (side === 'front') { setPhotoFrontUrl(url); setPhotoFront('existing'); }
              else { setPhotoBackUrl(url); setPhotoBack('existing'); }
            } else {
              if (side === 'front') { setPhotoFrontUrl(null); setPhotoFront(null); }
              else { setPhotoBackUrl(null); setPhotoBack(null); }
            }
          } catch (e) {
            // Network error or 404 — treat as no photo
            if (side === 'front') { setPhotoFrontUrl(null); setPhotoFront(null); }
            else { setPhotoBackUrl(null); setPhotoBack(null); }
          }
        };
        probe(fUrl, 'front');
        probe(bUrl, 'back');
        // Probe optional condition slots the same way (only mark present ones).
        CONDITION_SLOTS.forEach(({ slot }) => {
          const cUrl = imageUrl(user.id, cardKey, slot, version);
          fetch(cUrl, { method: 'GET', cache: 'no-store' })
            .then((resp) => {
              if (resp.ok) {
                setCondPhotoUrls((p) => ({ ...p, [slot]: cUrl }));
                setCondPhotos((p) => ({ ...p, [slot]: 'existing' }));
              }
            })
            .catch(() => { /* no photo for this slot */ });
        });
      }
    } else {
      // new entry defaults
      setStatus('collected');
      setParallelId(initialParallelId || 'base');
      setSerial(initialSerial ? String(initialSerial) : '');
      // Pre-loaded photos (e.g. from the AI scanner). Treated exactly like a
      // collector-picked File: pending upload + a preview URL.
      if (initialPhotoFront) { setPhotoFront(initialPhotoFront); setPhotoFrontUrl(URL.createObjectURL(initialPhotoFront)); }
      else { setPhotoFront(null); setPhotoFrontUrl(null); }
      if (initialPhotoBack) { setPhotoBack(initialPhotoBack); setPhotoBackUrl(URL.createObjectURL(initialPhotoBack)); }
      else { setPhotoBack(null); setPhotoBackUrl(null); }
      setCondPhotos({}); setCondPhotoUrls({});
      setSalePrice(''); setTradeValue(''); setTradeNotes(''); setWillingToPay('');
      setGradeCompany(''); setGradeValue(''); setGradeCertNum('');
      setGradeTagScore(''); setGradePrintRun('');
      setGradePopThis(''); setGradePopHigher(''); setGradeReportUrl('');
      setPrivateNote('');
    }
    setSaveError(null);
    return () => { if (noteFetchCancel.current) { noteFetchCancel.current(); noteFetchCancel.current = null; } };
  }, [isOpen, mode, existingEntry, initialParallelId, initialSerial, initialPhotoFront, initialPhotoBack, user]);

  // Pre-loaded (scanner) photos get the same auto-crop preview + undo as
  // picked ones; the raw preview above shows until this resolves.
  useEffect(() => {
    if (!isOpen || mode === 'edit') return undefined;
    let live = true;
    [['front', initialPhotoFront], ['back', initialPhotoBack]].forEach(([side, file]) => {
      if (!file) return;
      resizePhoto(file, 1600, { autoPortrait: !isLandscape, autoCrop: true }).then(({ blob, cropped }) => {
        if (!live || !cropped) return;
        const url = URL.createObjectURL(blob);
        if (side === 'front') setPhotoFrontUrl(url); else setPhotoBackUrl(url);
        setPhotoCrop((c) => ({ ...c, [side]: 'auto' }));
      });
    });
    return () => { live = false; };
  }, [isOpen, mode, initialPhotoFront, initialPhotoBack, isLandscape]);

  // --- Phase 2E.1 + 2E.2: load identity_verified + payment-method readiness ---
  // identity_verified drives the $500 gate hint. paymentMethods drives the
  // For-Sale-requires-≥1-method gate; "ready" means the toggle is on AND the
  // underlying handle/email is non-empty — a toggle alone isn't enough,
  // because the edge function would later reject the buyer with 409.
  useEffect(() => {
    if (!isOpen || !user?.id) {
      setIdentityVerified(null);
      setPaymentMethods(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('profiles')
        .select('identity_verified, accepts_paypal, paypal_email, accepts_venmo, venmo_username')
        .eq('id', user.id)
        .maybeSingle();
      if (!cancelled) {
        setIdentityVerified(!!data?.identity_verified);
        setPaymentMethods({
          paypal: !!data?.accepts_paypal && !!(data?.paypal_email || '').trim(),
          venmo:  !!data?.accepts_venmo  && !!(data?.venmo_username || '').trim(),
        });
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, user?.id]);

  // --- required field logic (single source of truth) ---
  const missingFields = useMemo(() => {
    const missing = [];
    if (!parallelId) missing.push('PARALLEL');
    if (serial.trim() !== '') {
      if (!photoFront) missing.push('FRONT PHOTO');
      if (!photoBack) missing.push('BACK PHOTO');
    }
    if (status === 'for_sale' && (!salePrice || parseFloat(salePrice) <= 0)) missing.push('PRICE');
    if (status === 'trade' && (!tradeValue || parseFloat(tradeValue) <= 0)) missing.push('TRADE VALUE');
    return missing;
  }, [parallelId, serial, photoFront, photoBack, status, salePrice, tradeValue]);

  const canSave = missingFields.length === 0 && !saving;

  // --- photo selection handlers ---
  // crop: 'auto' tries the auto-crop, 'off' keeps the photo as taken (undo).
  const handlePhotoSelect = async (side, file, crop = 'auto') => {
    if (!file) return;
    if (side === 'front') setPhotoFront(file); else setPhotoBack(file);
    // Preview a downscaled copy, not the raw file: full-res phone-camera JPEGs
    // can exceed the browser's <img> decode limit and render blank (the Android
    // "took the photo but it never showed up" bug). resizePhoto decodes via
    // createImageBitmap, which handles those; fall back to the raw file.
    let url;
    let next = null;
    try {
      const r = await resizePhoto(file, 1600, { autoPortrait: !isLandscape, autoCrop: crop === 'auto' });
      url = URL.createObjectURL(r.blob);
      next = crop === 'off' ? 'off' : (r.cropped ? 'auto' : null);
    } catch (_e) {
      url = URL.createObjectURL(file);
    }
    if (side === 'front') setPhotoFrontUrl(url); else setPhotoBackUrl(url);
    setPhotoCrop((c) => ({ ...c, [side]: next }));
  };
  const togglePhotoCrop = (side) => {
    const file = side === 'front' ? photoFront : photoBack;
    if (!(file instanceof Blob)) return;
    handlePhotoSelect(side, file, photoCrop[side] === 'auto' ? 'off' : 'auto');
  };
  const { zoneProps: photoDropZone, isTarget: isPhotoDropTarget } = usePhotoDrop(handlePhotoSelect, { front: !!photoFrontUrl, back: !!photoBackUrl });

  // --- remove photo (immediate, not deferred until save) ---
  // For 'edit' mode with an existing photo: deletes the storage object and
  // updates DB flags + photos_updated_at via handlePhotoRemove (cache fix).
  // For 'new' mode or local-only photos: just clears local state.
  const handleRemovePhoto = async (side) => {
    if (!window.confirm(`Remove ${side} photo?`)) return;
    const isPersisted = mode === 'edit' && existingEntry?.cardKey
      && (side === 'front' ? photoFront === 'existing' : photoBack === 'existing');
    if (isPersisted) {
      try {
        await handlePhotoRemove(
          user.id,
          existingEntry.cardKey,
          side,
          side === 'front' ? false : (photoFront === 'existing' || !!photoFront),
          side === 'back'  ? false : (photoBack  === 'existing' || !!photoBack)
        );
      } catch (err) {
        console.error('[CollectionModal] handleRemovePhoto error:', err);
        alert(`Failed to remove ${side} photo. Please try again.`);
        return;
      }
    }
    if (side === 'front') {
      setPhotoFront(null);
      setPhotoFrontUrl(null);
    } else {
      setPhotoBack(null);
      setPhotoBackUrl(null);
    }
    setPhotoCrop((c) => ({ ...c, [side]: null }));
  };

  // --- remove a condition photo (optional; never gates the listing) ---
  const handleRemoveCond = async (slot) => {
    if (!window.confirm(`Remove ${slot} photo?`)) return;
    const isPersisted = mode === 'edit' && existingEntry?.cardKey && condPhotos[slot] === 'existing';
    if (isPersisted) {
      try {
        await removeExtraPhoto(user.id, existingEntry.cardKey, slot);
      } catch (err) {
        console.error('[CollectionModal] handleRemoveCond error:', err);
        alert(`Failed to remove ${slot} photo. Please try again.`);
        return;
      }
    }
    setCondPhotos((p) => { const n = { ...p }; delete n[slot]; return n; });
    setCondPhotoUrls((p) => { const n = { ...p }; delete n[slot]; return n; });
  };

  // --- save ---
  const handleSave = useCallback(async () => {
    if (!canSave || !user?.id || !setMeta || !set || !card) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Phase 2E.2 § 5.2 Addendum: For Sale listings require ≥ 1 *fully
      // configured* payment method on the seller's profile. "Fully configured"
      // means the toggle is on AND the underlying handle/email is non-empty —
      // a toggle alone isn't enough, because the edge function would later
      // reject the buyer with 409. Block at the top of save BEFORE any photo
      // upload, BEFORE the $500 verification gate (so the message isn't masked).
      if (status === 'for_sale') {
        const { data: prof } = await supabase
          .from('profiles')
          .select('accepts_paypal, paypal_email, accepts_venmo, venmo_username')
          .eq('id', user.id)
          .maybeSingle();
        const paypalReady = !!prof?.accepts_paypal && !!(prof?.paypal_email || '').trim();
        const venmoReady  = !!prof?.accepts_venmo  && !!(prof?.venmo_username || '').trim();
        // Keep the modal's inline-hint state in sync with what we just learned.
        setPaymentMethods({ paypal: paypalReady, venmo: venmoReady });
        if (!paypalReady && !venmoReady) {
          setSaveError(
            'For Sale listings need at least one fully set-up payment method. Add a PayPal email or a Venmo handle on your profile, then save again.'
          );
          setSaving(false);
          return;
        }
      }

      let noteSaveFailed = false;

      // 1. Compute cardKey
      const setPath = `${setMeta.id}__${set.id}`;
      // Bug fix (2026-04-24): when parallelId is the 'base' sentinel (meaning
      // "no parallel selected"), write the plain key format used by the tile
      // renderer — NOT parallelKey() which appends '__parallel__base'. The
      // suffix caused saves to silently mismatch the render lookup: data was
      // persisted but the tile showed no indicator. Real parallels still
      // route through parallelKey() as before.
      let cardKey = (parallelId === 'base' || !parallelId)
        ? `${setPath}__${card.n}`
        : parallelKey(setPath, card.n, parallelId);

      // Serial suffix (tech debt — see docs/UNIFIED_MODAL_SPEC.md):
      // Whenever a serial is entered, card_key gets a __s__N suffix.
      // This matches the format the rest of the app uses to find serials
      // via startsWith(pKey + '__s__') patterns in Checklist.js, Gallery.js,
      // and PublicGallery.js.
      if (mode === 'new' && serial.trim() !== '') {
        cardKey = `${cardKey}__s__${serial.trim()}`;
      } else if (mode === 'edit' && existingEntry?.cardKey) {
        // If the existing row had NO serial suffix but the user is now
        // entering a serial, promote it to a serial row by appending the
        // __s__N suffix. The old base row stays in place (user can clean up).
        if (serial.trim() !== '' && !existingEntry.cardKey.includes('__s__')) {
          cardKey = `${existingEntry.cardKey}__s__${serial.trim()}`;
        } else {
          cardKey = existingEntry.cardKey; // preserve the existing key
        }
      }

      // 2. Upload photos (only if changed, i.e. not 'existing')
      // Note: photo REMOVAL is handled immediately by handleRemovePhoto (not here),
      // which routes through updateMarketplaceEligibility to stamp photos_updated_at.
      if (photoFront && photoFront !== 'existing') {
        const resized = await resizeImage(photoFront, 1600, { autoPortrait: !isLandscape, autoCrop: photoCrop.front === 'auto' });
        const { error: upErr } = await supabase.storage
          .from('card-images')
          .upload(imagePath(user.id, cardKey, 'front'), resized, {
            upsert: true,
            contentType: 'image/jpeg',
          });
        if (upErr) throw new Error(`Front photo upload: ${upErr.message}`);
      }
      if (photoBack && photoBack !== 'existing') {
        const resized = await resizeImage(photoBack, 1600, { autoPortrait: !isLandscape, autoCrop: photoCrop.back === 'auto' });
        const { error: upErr } = await supabase.storage
          .from('card-images')
          .upload(imagePath(user.id, cardKey, 'back'), resized, {
            upsert: true,
            contentType: 'image/jpeg',
          });
        if (upErr) throw new Error(`Back photo upload: ${upErr.message}`);
      }

      // 2b. Upload any pending condition close-ups (optional; same deferred
      // model as front/back). These never call updateMarketplaceEligibility —
      // condition shots never gate the listing.
      for (const { slot } of CONDITION_SLOTS) {
        const f = condPhotos[slot];
        if (f && f !== 'existing') {
          const resized = await resizeImage(f);
          const { error: upErr } = await supabase.storage
            .from('card-images')
            .upload(imagePath(user.id, cardKey, slot), resized, { upsert: true, contentType: 'image/jpeg' });
          if (upErr) throw new Error(`${slot} photo upload: ${upErr.message}`);
        }
      }

      // 3. Build DB payload
      const payload = {
        user_id: user.id,
        card_key: cardKey,
        status: status,
      };
      if (status === 'for_sale') payload.sale_price = parseFloat(salePrice);
      if (status === 'trade') {
        payload.trade_value = parseFloat(tradeValue);
        if (tradeNotes.trim()) payload.trade_notes = tradeNotes.trim();
      }
      if (status === 'wishlist') {
        // Optional; explicit null clears a previously-saved amount on edit.
        const wtp = parseFloat(willingToPay);
        payload.willing_to_pay = Number.isFinite(wtp) && wtp > 0 ? wtp : null;
      }

      // Phase 2E.1 § 5.6 Decision 6: gate $500+ For Sale publishes behind
      // identity verification. Every save explicitly sets is_paused so edits
      // (price drop, status change, post-verification) flip the flag back
      // to false automatically. Refetch identity_verified if still loading.
      let verifiedNow = identityVerified;
      if (verifiedNow === null) {
        const { data: prof } = await supabase
          .from('profiles')
          .select('identity_verified')
          .eq('id', user.id)
          .maybeSingle();
        verifiedNow = !!prof?.identity_verified;
        setIdentityVerified(verifiedNow);
      }
      const priceNum = status === 'for_sale' ? parseFloat(salePrice) : 0;
      const needsGate = status === 'for_sale'
        && priceNum > VERIFICATION_GATE_THRESHOLD
        && verifiedNow === false;
      payload.is_paused = needsGate;
      if (serial.trim() !== '') payload.grading_serial_num = serial.trim();
      if (gradeCompany) {
        payload.grading_company = gradeCompany;
        if (currentParallel.print_run) {
          payload.grading_print_run = String(currentParallel.print_run);
        }
        payload.grading_grade = gradeValue || null;
        payload.grading_cert_num = gradeCertNum || null;
        payload.grading_tag_score = gradeTagScore || null;
        payload.grading_pop_this = gradePopThis || null;
        payload.grading_pop_higher = gradePopHigher || null;
        payload.grading_report_url = gradeReportUrl || null;
      }

      // 4. Upsert (same pattern used elsewhere in the app)
      console.log('[CollectionModal] upsert payload:', JSON.stringify(payload, null, 2));
      const upsertResult = await supabase
        .from('collections')
        .upsert(payload, { onConflict: 'user_id,card_key' });
      console.log('[CollectionModal] upsert result:', upsertResult);
      if (upsertResult.error) throw new Error('Supabase: ' + upsertResult.error.message + ' (code ' + (upsertResult.error.code || '?') + ')');
      trackCardMarked(user.id, cardKey, status);

      // 4a. Private note — separate table, so a separate write. Deliberately
      // AFTER the collections upsert and non-fatal: a note that fails to save
      // must never cost the collector the card entry itself. Keyed on the FINAL
      // cardKey, so a note follows an entry that was just promoted to a serial.
      try {
        await saveNote(user.id, cardKey, privateNote);
      } catch (err) {
        console.warn('[CollectionModal] private note save failed (non-fatal):', err);
        noteSaveFailed = true;
      }

      // 4b. If any photos were freshly uploaded, stamp has_photos + photos_updated_at
      // via updateMarketplaceEligibility. This is what powers the cache-busting on
      // the checklist (?v= URL param). Skip if neither side was newly uploaded.
      const photoFrontUploaded = !!photoFront && photoFront !== 'existing';
      const photoBackUploaded  = !!photoBack  && photoBack  !== 'existing';
      if (photoFrontUploaded || photoBackUploaded) {
        const finalHasFront = !!photoFront; // includes 'existing'
        const finalHasBack  = !!photoBack;
        try {
          await updateMarketplaceEligibility(user.id, cardKey, finalHasFront, finalHasBack);
        } catch (err) {
          console.warn('[CollectionModal] updateMarketplaceEligibility (non-fatal):', err);
        }
      }

      // 5. Notify parent — branch on whether the verification gate intercepts.
      // The row is already saved (paused). We hand off to VerificationGateModal,
      // which will either unpause it (verification succeeds) or leave it paused
      // (user saved-for-later). In either branch we already notified that the
      // row was written.
      if (noteSaveFailed && !needsGate) {
        // Card is saved; only the note write failed. Stay open, and deliberately
        // do NOT call onSaved or onClose — closing would wipe both this message
        // and the note still sitting in the box.
        // Saving again re-runs the whole path and is safe: the collections write
        // is an upsert on (user_id, card_key).
        setSaveError('The card was saved, but the private note could not be. Your note is still here — tap save to try again.');
        return;
      }

      if (onSaved) onSaved({ cardKey, payload });
      if (needsGate) {
        setGateModalProps({ cardKey, priceUsd: priceNum });
        // Keep CollectionModal open under the gate so the user has context;
        // the gate's own onClose / onPublished handlers will close both.
      } else {
        onClose();
      }
    } catch (err) {
      console.error('CollectionModal save failed:', err);
      const msg = err.message || String(err) || 'Unknown save error';
      setSaveError(msg);
      alert('SAVE FAILED:\n\n' + msg);
    } finally {
      setSaving(false);
    }
  }, [canSave, user, setMeta, set, card, parallelId, mode, serial, existingEntry, privateNote, isLandscape,
      photoFront, photoBack, condPhotos, status, salePrice, tradeValue, tradeNotes, willingToPay,
      gradeCompany, gradeValue, gradeCertNum, gradeTagScore, gradePrintRun,
      gradePopThis, gradePopHigher, gradeReportUrl, onSaved, onClose,
      identityVerified, // Phase 2E.1: $500 gate decision depends on this
      paymentMethods]); // Phase 2E.2: For-Sale-requires-≥1-method gate

  if (!isOpen) return null;

  // =====================================================================
  // RENDER
  // =====================================================================
  return (
    <>
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: 'calc(20px + max(env(safe-area-inset-top, 0px), var(--ck-android-inset-top, 0px))) 10px 20px', overflowY: 'auto',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      {...swallowDrops /* a stray photo drop never opens the file in the tab */}
    >
      <div
        style={{
          background: C.cardBg,
          border: `1px solid ${C.border}`,
          borderTop: `2px solid ${C.gold}`,
          borderRadius: 3,
          width: '100%', maxWidth: 560,
          boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
          overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* HEADER */}
        <div style={{
          padding: '14px 16px',
          borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'linear-gradient(180deg, rgba(var(--gold-rgb),0.04), transparent)',
        }}>
          <div>
            <div style={{ fontFamily: fontDisplay, fontSize: 12, fontWeight: 700, letterSpacing: 2, color: C.gold }}>
              {mode === 'edit' ? 'EDIT COLLECTION ENTRY' : 'ADD TO COLLECTION'}
            </div>
            <div style={{ fontFamily: fontMono, fontSize: 10, color: C.textDim, marginTop: 2 }}>
              #{card?.n} · {card?.name}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 28, height: 28, background: 'transparent',
              border: `1px solid ${C.border}`, color: C.textDim,
              fontSize: 16, cursor: 'pointer', borderRadius: 2,
            }}
            aria-label="Close"
          >✕</button>
        </div>

        {/* BODY */}
        <div style={{
          padding: '20px 16px',
          display: 'flex', flexDirection: 'column', gap: 20,
          maxHeight: '70vh', overflowY: 'auto',
        }}>

          {/* ---- STATUS ---- */}
          <Section label="STATUS">
            <StatusPicker value={status} onChange={setStatus} />

            {status === 'for_sale' && (
              <ConditionalField color={C.purple}>
                <ConditionalLabel>PRICE <ReqBadge /></ConditionalLabel>
                <PriceRow dollar="$" dollarColor={C.purple}>
                  <input
                    type="number" step="0.01" min="0"
                    value={salePrice}
                    onChange={(e) => setSalePrice(e.target.value)}
                    placeholder="0.00"
                    style={inputStyle}
                  />
                </PriceRow>
                <div style={{
                  display: 'flex',
                  gap: 9,
                  alignItems: 'flex-start',
                  background: 'rgba(var(--green-rgb),0.06)',
                  border: '1px solid rgba(var(--green-rgb),0.28)',
                  borderRadius: 6,
                  padding: '10px 12px',
                  marginTop: 8,
                }}>
                  <span style={{ color: C.green, fontSize: 15, lineHeight: 1.3, flexShrink: 0 }}>✓</span>
                  <div>
                    <div style={{ fontFamily: fontBody, fontSize: 13, color: C.textDim, lineHeight: 1.4 }}>
                      Inclusive of shipping and vendor fees.
                    </div>
                    <div style={{ fontFamily: fontBody, fontSize: 13, fontWeight: 600, color: C.green, lineHeight: 1.4, marginTop: 1 }}>
                      Collection Keeper never takes a cent from any transaction.
                    </div>
                  </div>
                </div>
                {/* Phase 2E.1 § 5.6 sub-point 6: quiet invitation for unverified */}
                {identityVerified === false && (() => {
                  const p = parseFloat(salePrice);
                  const isHigh = Number.isFinite(p) && p > VERIFICATION_GATE_THRESHOLD;
                  return (
                    <div style={{
                      fontFamily: fontMono,
                      fontSize: 11,
                      color: isHigh ? 'var(--warn)' : 'var(--muted-5)',
                      marginTop: 8,
                      lineHeight: 1.5,
                    }}>
                      {isHigh ? (
                        <>🛡 Listings over ${VERIFICATION_GATE_THRESHOLD} require ID verification. We'll prompt you to verify when you save — your draft will be kept safe.</>
                      ) : (
                        <>Listings under ${VERIFICATION_GATE_THRESHOLD} don't require verification. Want to show <strong style={{ color: 'var(--ok)' }}>✓ VERIFIED</strong> on your listings?{' '}
                          <a href="/profile" style={{ color: 'var(--violet)', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>Verify on your profile →</a>
                        </>
                      )}
                    </div>
                  );
                })()}
                {/* Phase 2E.2 § 5.2 Addendum: friendly invitation when the seller
                    has no payment methods set. Save-time gate is the hard stop;
                    this hint is the early signal. Amber matches the existing
                    over-$500 verification hint above. */}
                {paymentMethods && !paymentMethods.paypal && !paymentMethods.venmo && (
                  <div style={{
                    fontFamily: fontMono,
                    fontSize: 11,
                    color: 'var(--warn)',
                    marginTop: 8,
                    lineHeight: 1.5,
                  }}>
                    💰 For Sale listings need at least one payment method.{' '}
                    <a href="/profile" style={{ color: 'var(--violet)', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>Add PayPal or Venmo on your profile →</a>
                  </div>
                )}
              </ConditionalField>
            )}

            {status === 'trade' && (
              <ConditionalField color={C.cyan}>
                <ConditionalLabel>TRADE VALUE <ReqBadge /></ConditionalLabel>
                <PriceRow dollar="$" dollarColor={C.cyan}>
                  <input
                    type="number" step="0.01" min="0"
                    value={tradeValue}
                    onChange={(e) => setTradeValue(e.target.value)}
                    placeholder="0.00"
                    style={inputStyle}
                  />
                </PriceRow>
                <textarea
                  value={tradeNotes}
                  onChange={(e) => setTradeNotes(e.target.value)}
                  placeholder="Trade notes (optional) — what are you looking for?"
                  style={{ ...inputStyle, marginTop: 6, fontFamily: fontBody, minHeight: 60, resize: 'vertical' }}
                />
              </ConditionalField>
            )}

            {status === 'wishlist' && (
              <ConditionalField color={C.gold}>
                <ConditionalLabel>WILLING TO PAY <span style={{ color: C.textDim, fontWeight: 400 }}>(OPTIONAL)</span></ConditionalLabel>
                <PriceRow dollar="$" dollarColor={C.gold}>
                  <input
                    type="number" step="0.01" min="0"
                    value={willingToPay}
                    onChange={(e) => setWillingToPay(e.target.value)}
                    placeholder="0.00"
                    style={inputStyle}
                  />
                </PriceRow>
                <div style={{ fontFamily: fontMono, fontSize: 11, color: C.textDim, marginTop: 8, lineHeight: 1.5 }}>
                  What you'd pay for a <strong style={{ color: C.gold }}>Mint / Gem Mint</strong> copy. Shown with your wishlist on the Marketplace WANTED board so sellers know your offer.
                </div>
              </ConditionalField>
            )}
          </Section>

          {/* ---- PHOTO ---- */}
          <BoxedSection color={C.green} label="PHOTO" required={serial.trim() !== '' && (!photoFront || !photoBack)}>
            {serial.trim() !== '' && (!photoFront || !photoBack) && (
              <RedNotice>
                Because you entered a serial #, both FRONT and BACK photos are required for Community Checklist verification.
              </RedNotice>
            )}
            {(status === 'for_sale' || status === 'trade') && (!photoFront || !photoBack) && (
              <div style={{
                display: 'flex', gap: 8, alignItems: 'flex-start',
                background: 'rgba(var(--gold-rgb),0.08)', border: `1px solid ${C.gold}`,
                borderRadius: 4, padding: '8px 10px',
                fontFamily: fontBody, fontSize: 12, color: C.gold, lineHeight: 1.45,
              }}>
                <span style={{ fontSize: 13, lineHeight: 1 }}>⚠</span>
                <span>
                  This {status === 'for_sale' ? 'listing' : 'trade'} won’t appear in the Marketplace until it has
                  both a <b>FRONT</b> and a <b>BACK</b> photo. (Condition close-ups are optional and don’t count.)
                </span>
              </div>
            )}
            {/* Booklet cards stack the two wide frames full-width; singles keep the side-by-side pair. */}
            <div style={{ display: 'grid', gridTemplateColumns: isLandscape ? '1fr' : '1fr 1fr', gap: 10 }} {...photoDropZone}>
              <PhotoBox
                side="FRONT"
                droppable
                dropTarget={isPhotoDropTarget('front')}
                url={photoFrontUrl}
                wide={isLandscape}
                required={serial.trim() !== '' && !photoFront}
                onClick={() => {
                  // iPhone: open native iOS sheet (Photo Library / Take Photo / Choose File) directly.
                  // Android: open CK chooser (Take Photo / Choose From Library).
                  if (isIOS) {
                    if (frontInputRef.current) {
                      frontInputRef.current.value = '';
                      frontInputRef.current.removeAttribute('capture');
                      frontInputRef.current.click();
                    }
                  } else {
                    setPhotoChooser('front');
                  }
                }}
              />
              <PhotoBox
                side="BACK"
                droppable
                dropTarget={isPhotoDropTarget('back')}
                url={photoBackUrl}
                wide={isLandscape}
                required={serial.trim() !== '' && !photoBack}
                onClick={() => {
                  if (isIOS) {
                    if (backInputRef.current) {
                      backInputRef.current.value = '';
                      backInputRef.current.removeAttribute('capture');
                      backInputRef.current.click();
                    }
                  } else {
                    setPhotoChooser('back');
                  }
                }}
              />
            </div>
            {/* Auto-crop note + undo/redo, per side, only for a photo we cropped (or one the collector un-cropped). */}
            {(photoCrop.front || photoCrop.back) && (
              <div style={{ display: 'grid', gridTemplateColumns: isLandscape ? '1fr' : '1fr 1fr', gap: 10, marginTop: 6 }}>
                {['front', 'back'].map((side) => (
                  <div key={side} style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: 0.5, color: C.textDim, textAlign: 'center' }}>
                    {photoCrop[side] && (
                      <>
                        {photoCrop[side] === 'auto' ? 'Auto-cropped · ' : 'Original · '}
                        <button
                          type="button"
                          onClick={() => togglePhotoCrop(side)}
                          style={{ background: 'none', border: 'none', padding: 0, color: C.cyan, fontFamily: fontMono, fontSize: 10, letterSpacing: 0.5, cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          {photoCrop[side] === 'auto' ? 'Undo' : 'Crop'}
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
            {/* REMOVE photo buttons — appear only when a photo is present, on either side */}
            {(photoFrontUrl || photoBackUrl) && (
              <div style={{ display: 'grid', gridTemplateColumns: isLandscape ? '1fr' : '1fr 1fr', gap: 10, marginTop: 8 }}>
                <div>
                  {photoFrontUrl && (
                    <button
                      type="button"
                      onClick={() => handleRemovePhoto('front')}
                      style={{
                        display: 'block', width: '100%', padding: '8px 10px',
                        background: 'transparent', border: '1px solid rgba(var(--red-2-rgb),0.5)',
                        color: 'var(--red-2)', fontFamily: fontDisplay, fontSize: 10,
                        letterSpacing: 2, textTransform: 'uppercase',
                        borderRadius: 4, cursor: 'pointer',
                      }}
                    >
                      Remove Front
                    </button>
                  )}
                </div>
                <div>
                  {photoBackUrl && (
                    <button
                      type="button"
                      onClick={() => handleRemovePhoto('back')}
                      style={{
                        display: 'block', width: '100%', padding: '8px 10px',
                        background: 'transparent', border: '1px solid rgba(var(--red-2-rgb),0.5)',
                        color: 'var(--red-2)', fontFamily: fontDisplay, fontSize: 10,
                        letterSpacing: 2, textTransform: 'uppercase',
                        borderRadius: 4, cursor: 'pointer',
                      }}
                    >
                      Remove Back
                    </button>
                  )}
                </div>
              </div>
            )}
            {/* ---- CONDITION DETAIL (optional) ---- */}
            <div style={{ height: 1, background: 'rgba(var(--green-rgb),0.18)', margin: '4px 0 2px' }} />
            <div style={{ ...sectionLabelStyle(C.green), borderBottom: 'none', paddingBottom: 0, fontSize: 10, letterSpacing: 1.5, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>CONDITION DETAIL</span>
              <span style={{ fontFamily: fontMono, fontSize: 8, letterSpacing: 1, color: C.muted, border: `1px solid ${C.muted}`, borderRadius: 2, padding: '1px 5px' }}>OPTIONAL</span>
            </div>
            <div style={{ fontFamily: fontBody, fontSize: 12, color: C.textDim, lineHeight: 1.5 }}>
              Add close-ups so buyers can judge condition — each corner, the borders, and the front &amp; back surface. All optional, but more photos help cards sell faster (especially raw copies).
            </div>
            {CONDITION_CATEGORIES.map(({ name, cols }) => {
              const slots = CONDITION_SLOTS.filter((s) => s.category === name);
              const filledCount = slots.filter((s) => condPhotoUrls[s.slot]).length;
              return (
                <div key={name} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontFamily: fontMono, fontSize: 9, letterSpacing: 1.5, color: C.textDim, textTransform: 'uppercase' }}>
                    {name} <span style={{ color: C.muted }}>· {filledCount}/{slots.length}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 6 }}>
                    {slots.map(({ slot, short }) => (
                      <div key={slot} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <PhotoBox
                          side={short.toUpperCase()}
                          url={condPhotoUrls[slot] || null}
                          required={false}
                          onClick={() => openCondPicker(slot)}
                        />
                        {condPhotoUrls[slot] && (
                          <button
                            type="button"
                            onClick={() => handleRemoveCond(slot)}
                            style={{
                              width: '100%', padding: '4px', background: 'transparent',
                              border: '1px solid rgba(var(--red-2-rgb),0.5)', color: 'var(--red-2)',
                              fontFamily: fontDisplay, fontSize: 8, letterSpacing: 1,
                              textTransform: 'uppercase', borderRadius: 4, cursor: 'pointer',
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {/* condition-slot hidden inputs + chooser (from the shared usePhotoSource) */}
            {condInputs}
            {condChooser}

            {/* iPhone hidden inputs — single input per side; iOS native sheet handles source selection */}
            <input
              ref={frontInputRef}
              type="file" accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => handlePhotoSelect('front', e.target.files?.[0])}
            />
            <input
              ref={backInputRef}
              type="file" accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => handlePhotoSelect('back', e.target.files?.[0])}
            />
            {/* Android dedicated inputs — one always-camera, one always-library, per side.
                Avoids the bug where mutating `capture` between clicks races on Android Chrome. */}
            <input
              ref={frontCameraInputRef}
              type="file" accept="image/*" capture="environment"
              style={{ display: 'none' }}
              onChange={(e) => handlePhotoSelect('front', e.target.files?.[0])}
            />
            <input
              ref={frontLibraryInputRef}
              type="file" accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => handlePhotoSelect('front', e.target.files?.[0])}
            />
            <input
              ref={backCameraInputRef}
              type="file" accept="image/*" capture="environment"
              style={{ display: 'none' }}
              onChange={(e) => handlePhotoSelect('back', e.target.files?.[0])}
            />
            <input
              ref={backLibraryInputRef}
              type="file" accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => handlePhotoSelect('back', e.target.files?.[0])}
            />
            {/* Photo source chooser — Android/desktop only. iPhone uses native iOS sheet directly. */}
            {photoChooser && !isIOS && (
              <div
                onClick={() => setPhotoChooser(null)}
                style={{
                  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 9999, padding: 20,
                }}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    background: 'var(--panel)', border: '1px solid var(--cyan)',
                    borderRadius: 12, padding: 24, width: '100%', maxWidth: 360,
                    boxShadow: '0 0 24px rgba(var(--cyan-rgb),0.3)',
                  }}
                >
                  <div style={{
                    fontFamily: fontDisplay, color: 'var(--cyan)',
                    fontSize: 14, letterSpacing: 2, textAlign: 'center',
                    marginBottom: 18, textTransform: 'uppercase',
                  }}>
                    {(photoChooser === 'front' ? photoFrontUrl : photoBackUrl) ? 'Update' : 'Add'} {photoChooser} Photo
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const ref = photoChooser === 'front' ? frontCameraInputRef : backCameraInputRef;
                      if (ref.current) {
                        // Clear value so Android Chrome re-opens the picker even after a previous back-out.
                        ref.current.value = '';
                        ref.current.click();
                      }
                      setPhotoChooser(null);
                    }}
                    style={{
                      display: 'block', width: '100%', padding: '14px 20px',
                      marginBottom: 12, background: 'transparent',
                      border: '1px solid var(--gold)', color: 'var(--gold)',
                      fontFamily: fontBody, fontSize: 16,
                      fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase',
                      borderRadius: 8, cursor: 'pointer',
                    }}
                  >
                    📷 Take Photo
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const ref = photoChooser === 'front' ? frontLibraryInputRef : backLibraryInputRef;
                      if (ref.current) {
                        // Clear value so Android Chrome re-opens the picker even after a previous back-out.
                        ref.current.value = '';
                        ref.current.click();
                      }
                      setPhotoChooser(null);
                    }}
                    style={{
                      display: 'block', width: '100%', padding: '14px 20px',
                      marginBottom: 12, background: 'transparent',
                      border: '1px solid var(--cyan)', color: 'var(--cyan)',
                      fontFamily: fontBody, fontSize: 16,
                      fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase',
                      borderRadius: 8, cursor: 'pointer',
                    }}
                  >
                    🖼️ Choose From Library
                  </button>
                  <button
                    type="button"
                    onClick={() => setPhotoChooser(null)}
                    style={{
                      display: 'block', width: '100%', padding: '12px 20px',
                      background: 'transparent', border: '1px solid var(--muted-2)',
                      color: 'var(--muted-2)', fontFamily: fontBody,
                      fontSize: 14, fontWeight: 600, letterSpacing: 1.5,
                      textTransform: 'uppercase', borderRadius: 8, cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </BoxedSection>

          {/* ---- PARALLEL ---- */}
          <BoxedSection color={C.orange} label="PARALLEL" required>
            {mode === 'edit' ? (
              <LockedParallel parallel={currentParallel} />
            ) : (
              <ParallelDropdown
                parallels={parallels}
                selected={currentParallel}
                open={parallelDropdownOpen}
                onToggle={() => setParallelDropdownOpen(!parallelDropdownOpen)}
                onSelect={(p) => {
                  setParallelId(p.id);
                  setParallelDropdownOpen(false);
                  // If new parallel isn't serialized, clear serial
                  if (!(p.print_run && p.print_run > 1)) setSerial('');
                }}
              />
            )}
          </BoxedSection>

          {/* ---- SERIAL # (only if serialized parallel) ---- */}
          {isSerializedParallel && (
            <BoxedSection color={C.gold} label="SERIAL #">
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="number" min="1" max={currentParallel.print_run}
                  value={serial}
                  onChange={(e) => setSerial(e.target.value)}
                  placeholder="1"
                  style={{ ...inputStyle, flex: 1, fontSize: 14, letterSpacing: 1 }}
                />
                <div style={{
                  display: 'flex', alignItems: 'center',
                  fontFamily: fontMono, fontSize: 14, color: C.textDim,
                  padding: '0 10px', background: 'rgba(0,0,0,0.2)',
                  border: `1px solid ${C.muted}`, borderRadius: 2,
                }}>/ {currentParallel.print_run}</div>
              </div>
              <div style={{ fontFamily: fontMono, fontSize: 10, color: C.gold, lineHeight: 1.5 }}>
                ★ Add a serial # to appear in the Community Checklist when your profile is Public
              </div>
            </BoxedSection>
          )}

          {/* ---- GRADING ---- */}
          <BoxedSection color={C.cyan} label="GRADING">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {GRADING_COMPANIES.map(co => (
                <button
                  key={co}
                  onClick={() => setGradeCompany(gradeCompany === co ? '' : co)}
                  style={chipStyle(gradeCompany === co, C.cyan)}
                >{co}</button>
              ))}
            </div>
            {gradeCompany && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <input
                    value={gradeValue}
                    onChange={(e) => setGradeValue(e.target.value)}
                    placeholder="Grade (e.g., 10, 9.5)"
                    style={inputStyle}
                  />
                  <input
                    value={gradeCertNum}
                    onChange={(e) => setGradeCertNum(e.target.value)}
                    placeholder="Cert #"
                    style={inputStyle}
                  />
                </div>
                <button
                  onClick={() => setAdvancedGradingOpen(!advancedGradingOpen)}
                  style={{
                    fontFamily: fontMono, fontSize: 10, color: C.cyan,
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    padding: '4px 0', textAlign: 'left', letterSpacing: 1,
                  }}
                >{advancedGradingOpen ? '− HIDE' : '+ SHOW'} ADVANCED FIELDS</button>
                {advancedGradingOpen && (
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
                    padding: 10, background: 'rgba(0,0,0,0.2)',
                    border: `1px solid ${C.border}`, borderRadius: 2,
                  }}>
                    <AdvField label="TAG SCORE" value={gradeTagScore} onChange={setGradeTagScore} />
                    <AdvField label="POP THIS GRADE" value={gradePopThis} onChange={setGradePopThis} />
                    <AdvField label="POP HIGHER" value={gradePopHigher} onChange={setGradePopHigher} />
                    <div style={{ gridColumn: '1/-1' }}>
                      <AdvField label="GRADE REPORT URL" value={gradeReportUrl} onChange={setGradeReportUrl} />
                    </div>
                  </div>
                )}
              </>
            )}
          </BoxedSection>

          {/* ---- PRIVATE NOTES ---- */}
          <BoxedSection color={C.steel} label="&#128274; PRIVATE NOTES">
            <textarea
              value={privateNote}
              onChange={(e) => setPrivateNote(e.target.value.slice(0, NOTE_MAX))}
              maxLength={NOTE_MAX}
              placeholder="Where it's stored, what you paid, anything you want to remember."
              style={{
                ...inputStyle,
                fontFamily: fontBody,
                fontSize: 14,
                lineHeight: 1.5,
                minHeight: 74,
                resize: 'vertical',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <div style={{ flex: 1, fontFamily: fontMono, fontSize: 10, lineHeight: 1.5, color: C.steel }}>
                Only you can see this. Never shown on the Community Checklist, the Marketplace, or your public profile.
              </div>
              <div style={{
                fontFamily: fontMono, fontSize: 10, whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
                color: privateNote.length > NOTE_MAX - 50 ? C.gold : C.textDim,
              }}>
                {privateNote.length} / {NOTE_MAX}
              </div>
            </div>
          </BoxedSection>

          {saveError && (
            <div style={{
              padding: '10px 12px',
              background: 'rgba(var(--red-2-rgb),0.08)',
              border: `1px solid ${C.red}`,
              borderRadius: 2,
              color: C.red,
              fontFamily: fontMono, fontSize: 11,
            }}>
              ⚠ {saveError}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div style={{
          padding: '14px 16px',
          borderTop: `1px solid ${C.border}`,
          display: 'flex', gap: 10, justifyContent: 'flex-end',
          background: 'rgba(0,0,0,0.3)',
        }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              fontFamily: fontMono, fontSize: 11,
              padding: '10px 18px', borderRadius: 2,
              cursor: saving ? 'not-allowed' : 'pointer',
              letterSpacing: 1,
              border: `1px solid ${C.muted}`, color: C.textDim,
              background: 'transparent',
              opacity: saving ? 0.5 : 1,
            }}
          >CANCEL</button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            style={{
              fontFamily: fontMono, fontSize: 11,
              padding: '10px 18px', borderRadius: 2,
              cursor: canSave ? 'pointer' : 'not-allowed',
              letterSpacing: 1,
              border: `1px solid ${canSave ? C.green : C.muted}`,
              color: canSave ? C.green : C.muted,
              background: canSave ? 'rgba(var(--green-rgb),0.08)' : 'transparent',
            }}
          >
            {saving
              ? 'SAVING...'
              : missingFields.length > 0
              ? `SAVE · MISSING ${missingFields.join(', ')}`
              : (mode === 'edit' ? 'SAVE CHANGES' : 'SAVE TO COLLECTION')}
          </button>
        </div>
      </div>
    </div>

    {/* Phase 2E.1 § 5.6 Decision 6: $500+ verification gate (renders above CollectionModal) */}
    {gateModalProps && (
      <VerificationGateModal
        user={user}
        cardKey={gateModalProps.cardKey}
        priceUsd={gateModalProps.priceUsd}
        onClose={() => {
          // Save-draft-for-later (or X): draft stays paused, close both modals.
          setGateModalProps(null);
          onClose();
        }}
        onPublished={() => {
          // Verification succeeded + listing unpaused: close both modals + refresh parent.
          setGateModalProps(null);
          if (onSaved) onSaved({ cardKey: gateModalProps.cardKey, payload: { is_paused: false } });
          onClose();
        }}
      />
    )}
    </>
  );
}

// =====================================================================
// Sub-components
// =====================================================================

function Section({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={sectionLabelStyle(C.textDim)}>{label}</div>
      {children}
    </div>
  );
}

function BoxedSection({ color, label, required, children }) {
  return (
    <div style={{
      padding: 14, borderRadius: 3,
      border: `1px solid ${color}`,
      background: `${color}08`,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{
        ...sectionLabelStyle(color),
        borderBottomColor: `${color}33`,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span>{label}</span>
        {required && <ReqBadge />}
      </div>
      {children}
    </div>
  );
}

function StatusPicker({ value, onChange }) {
  const options = [
    { id: 'collected', icon: '✓', label: 'COLLECTED', color: C.green },
    { id: 'wishlist',  icon: '★', label: 'WISHLIST',  color: C.gold },
    { id: 'trade',     icon: '⇄', label: 'TRADE',     color: C.cyan },
    { id: 'for_sale',  icon: '$', label: 'FOR SALE',  color: C.purple },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
      {options.map(opt => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            onClick={() => onChange(opt.id)}
            style={{
              background: active ? `${opt.color}15` : 'transparent',
              border: `1px solid ${active ? opt.color : C.border}`,
              color: active ? opt.color : C.textDim,
              padding: '10px 4px',
              cursor: 'pointer', borderRadius: 2,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            }}
          >
            <div style={{ fontSize: 16 }}>{opt.icon}</div>
            <div style={{ fontFamily: fontMono, fontSize: 9, letterSpacing: 1 }}>{opt.label}</div>
          </button>
        );
      })}
    </div>
  );
}

// wide: booklet cards (isLandscapeCard) get a 2:1 frame with the whole spread
// visible (contain) instead of the single-card 5/7 cover-crop.
// droppable: the front/back pair, which sits in the drag-and-drop zone.
// Condition close-ups reuse this box but take clicks only.
function PhotoBox({ side, url, required, onClick, wide = false, droppable = false, dropTarget = false }) {
  const dropHint = droppable && canDropPhotos;
  const borderColor = dropTarget ? C.cyan : required ? C.red : (url ? C.green : C.muted);
  return (
    <div
      data-side={droppable ? side.toLowerCase() : undefined}
      onClick={onClick}
      style={{
        aspectRatio: wide ? '2/1' : '5/7',
        background: dropTarget ? 'rgba(var(--cyan-rgb),0.08)' : '#000',
        border: `${dropTarget ? 2 : 1}px ${url || dropTarget ? 'solid' : 'dashed'} ${borderColor}`,
        borderRadius: 3,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        color: required ? C.red : (url ? C.green : C.textDim),
        cursor: 'pointer',
        position: 'relative', overflow: 'hidden',
      }}
    >
      {url ? (
        <>
          <img
            src={url} alt={side}
            style={{ width: '100%', height: '100%', objectFit: wide ? 'contain' : 'cover' }}
          />
          <div style={{
            position: 'absolute', top: 6, left: 6,
            fontFamily: fontMono, fontSize: 9,
            color: C.green, background: 'rgba(0,0,0,0.7)',
            padding: '2px 6px', borderRadius: 2,
          }}>{side}</div>
          {dropTarget && <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.6)', color: '#fff', fontFamily: fontMono, fontSize: 10, letterSpacing: 1,
          }}>DROP TO REPLACE</div>}
        </>
      ) : (
        <>
          <div style={{ fontSize: 32, marginBottom: 4, lineHeight: 1 }}>+</div>
          <div style={{ fontFamily: fontMono, fontSize: 9, letterSpacing: 1 }}>{side}</div>
          <div style={{ fontFamily: fontMono, fontSize: 8, marginTop: 4, letterSpacing: 0.5 }}>
            {dropTarget ? 'DROP IT HERE' : required ? (dropHint ? 'REQUIRED · CLICK OR DROP' : 'REQUIRED') : (dropHint ? 'CLICK OR DROP A PHOTO' : 'TAP TO ADD')}
          </div>
        </>
      )}
    </div>
  );
}

function ParallelDropdown({ parallels, selected, open, onToggle, onSelect }) {
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          background: 'rgba(0,0,0,0.4)',
          border: `1px solid ${C.orange}`,
          color: C.text,
          padding: '12px 14px',
          fontFamily: fontMono, fontSize: 13, letterSpacing: 1,
          borderRadius: open ? '2px 2px 0 0' : 2,
          cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', gap: 10,
        }}
      >
        <span style={{ color: C.orange, flex: 1, textAlign: 'left' }}>
          {selected.name.toUpperCase()}
        </span>
        <span style={{ color: selected.print_run ? C.gold : C.textDim, fontSize: 11 }}>
          {selected.print_run ? `/ ${selected.print_run}` : '—'}
        </span>
        <span style={{ color: C.orange, fontSize: 10, transform: open ? 'rotate(180deg)' : 'none' }}>▼</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: 'var(--panel-deep2)',
          border: `1px solid ${C.orange}`, borderTop: 'none',
          borderRadius: '0 0 2px 2px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.6)',
          maxHeight: 280, overflowY: 'auto',
          zIndex: 10,
        }}>
          {parallels.length === 0 ? (
            <div style={{ padding: 12, fontFamily: fontMono, fontSize: 11, color: C.textDim, textAlign: 'center' }}>
              No parallels found for this set
            </div>
          ) : parallels.map(p => (
            <button
              key={p.id}
              onClick={() => onSelect(p)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%',
                padding: '10px 14px',
                fontFamily: fontMono, fontSize: 12, letterSpacing: 1,
                cursor: 'pointer',
                color: selected.id === p.id ? C.orange : C.text,
                background: selected.id === p.id ? 'rgba(var(--sketch-orange-rgb),0.15)' : 'transparent',
                border: 'none',
                borderBottom: `1px solid rgba(var(--slate-rgb),0.15)`,
                textAlign: 'left',
              }}
            >
              <span style={{ flex: 1 }}>{p.name.toUpperCase()}</span>
              <span style={{ color: p.print_run ? C.gold : C.textDim, fontSize: 11 }}>
                {p.print_run ? `/ ${p.print_run}` : '—'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LockedParallel({ parallel }) {
  return (
    <>
      <div style={{
        background: 'rgba(0,0,0,0.3)',
        border: `1px solid rgba(var(--sketch-orange-rgb),0.4)`,
        borderLeft: `2px solid ${C.orange}`,
        padding: '10px 12px',
        borderRadius: 2,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <div style={{ width: 10, height: 10, background: C.orange, borderRadius: '50%' }} />
        <div style={{ fontFamily: fontMono, fontSize: 13, color: C.orange, letterSpacing: 1, flex: 1 }}>
          {parallel.name.toUpperCase()}
        </div>
        {parallel.print_run && (
          <div style={{ fontFamily: fontMono, fontSize: 11, color: C.textDim }}>
            / {parallel.print_run}
          </div>
        )}
        <div style={{ color: C.textDim, fontSize: 12 }}>🔒</div>
      </div>
      <div style={{ fontFamily: fontMono, fontSize: 9, color: C.textDim, letterSpacing: 1, marginTop: 4 }}>
        locked · to change parallel, delete this entry and re-add
      </div>
    </>
  );
}

function ReqBadge() {
  return (
    <span style={{
      fontFamily: fontMono, fontSize: 8, fontWeight: 400, letterSpacing: 1,
      color: C.red,
      background: 'rgba(var(--red-2-rgb),0.1)',
      border: `1px solid rgba(var(--red-2-rgb),0.35)`,
      padding: '1px 5px', borderRadius: 2,
    }}>REQUIRED</span>
  );
}

function RedNotice({ children }) {
  return (
    <div style={{
      fontFamily: fontMono, fontSize: 10,
      color: C.red,
      padding: '6px 8px',
      background: 'rgba(var(--red-2-rgb),0.06)',
      borderLeft: `2px solid ${C.red}`,
      borderRadius: 2, lineHeight: 1.5,
    }}>{children}</div>
  );
}

function ConditionalField({ color, children }) {
  return (
    <div style={{
      padding: 10, marginTop: 8,
      background: `${color}0d`,
      border: `1px solid ${color}40`,
      borderLeft: `2px solid ${color}`,
      borderRadius: 2,
    }}>{children}</div>
  );
}

function ConditionalLabel({ children }) {
  return (
    <div style={{
      fontFamily: fontMono, fontSize: 9,
      color: C.red, letterSpacing: 1,
      display: 'flex', alignItems: 'center', gap: 6,
      marginBottom: 4,
    }}>{children}</div>
  );
}

function PriceRow({ dollar, dollarColor, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'stretch', gap: 6 }}>
      <div style={{
        padding: '8px 10px',
        background: `${dollarColor}26`,
        border: `1px solid ${C.muted}`,
        color: dollarColor,
        fontFamily: fontMono, fontSize: 13,
        borderRadius: 2,
      }}>{dollar}</div>
      {children}
    </div>
  );
}

function AdvField({ label, value, onChange }) {
  return (
    <div>
      <label style={{
        fontFamily: fontMono, fontSize: 9,
        color: C.textDim, letterSpacing: 1,
        display: 'block', marginBottom: 2,
      }}>{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle, width: '100%' }}
      />
    </div>
  );
}

// ---- shared inline styles ----
const inputStyle = {
  background: 'rgba(0,0,0,0.4)',
  border: `1px solid ${C.muted}`,
  color: C.text,
  fontFamily: fontMono,
  fontSize: 13,
  padding: '8px 10px',
  borderRadius: 2,
  outline: 'none',
};

function sectionLabelStyle(color) {
  return {
    fontFamily: fontDisplay,
    fontSize: 10, fontWeight: 700, letterSpacing: 2,
    color,
    textTransform: 'uppercase',
    paddingBottom: 4,
    borderBottom: `1px solid rgba(var(--slate-rgb),0.2)`,
  };
}

function chipStyle(selected, color) {
  return {
    fontFamily: fontMono, fontSize: 11,
    padding: '6px 12px',
    border: `1px solid ${selected ? color : C.muted}`,
    color: selected ? color : C.textDim,
    background: selected ? `${color}15` : 'transparent',
    borderRadius: 2,
    cursor: 'pointer',
    letterSpacing: 1,
  };
}
